import axios from "axios";
import moment from "moment-timezone";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SortConfig } from "src/helpers/api/sortconfig";

import { FiltersFieldProps } from "src/components/dashboard/Filters";
import PastPerformanceTable, {
  PerformanceTab,
} from "src/components/pastPerformance/PastPerformanceTable";
import PerformanceHeatmap, {
  PerformanceControls,
} from "src/components/pastPerformance/PerformanceHeatmap";
import PerformanceStats from "src/components/pastPerformance/PerformanceStats";
import IndexComparisonChart, {
  IndexChartSeries,
} from "src/components/pastPerformance/IndexComparisonChart";
import { useTheme } from "src/components/layouts/contexts/ThemeContext";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import {
  clampMonthToRange,
  isDateInRange,
  monthIntersectsRange,
} from "src/helpers/performance-month";
import { useCustomUniverses } from "src/hooks/useCustomUniverses";
import { alertService, performanceService } from "src/services";

interface HeatmapDataPoint {
  date: string;
  avg_peak_return: number;
  avg_current_return: number;
}

interface PerformanceRecord {
  symbol: string;
  date: string;
  indicesname?: string;
  close: number;
  peak_return: number;
  current_return: number;
}

interface PerformanceHeader {
  market_performance: number;
  no_of_stock: number;
  avg_peak_return: number;
  avg_current_return: number;
}

interface TopPerformer {
  symbol: string;
  date: string;
  indicesname?: string;
  close: number;
  peak_return: number;
  current_return: number;
}

interface IndexPerformanceItem {
  symbol: string;
  label: string;
  return_pct: number | null;
}

interface IndexChartData {
  dates: string[];
  series: IndexChartSeries[];
}

/** Below this the page scrolls rather than crushing the tables to nothing. */
const MIN_PAGE_HEIGHT = 560;

/** The Top Performers tab is a complete but capped list; the export matches it. */
const TOP_PERFORMERS_LIMIT = 200;

interface FetchParams {
  page?: number;
  limit?: number;
  fromDate?: string;
  toDate?: string;
  symbol?: string;
  universe_name?: string;
  indicesname?: string;
  date?: string;
  [key: string]: string | number | boolean | undefined;
}

const PastPerformancePage = () => {
  const [heatmapData, setHeatmapData] = useState<HeatmapDataPoint[]>([]);
  const [records, setRecords] = useState<PerformanceRecord[]>([]);
  const [header, setHeader] = useState<PerformanceHeader | null>(null);
  const [topPerformers, setTopPerformers] = useState<TopPerformer[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [heatmapDate, setHeatmapDate] = useState<string | null>(null);
  /** A whole-month drill-down, mutually exclusive with `heatmapDate`. Format matches the heatmap's month id: "MMM-YYYY". */
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [filters, setFilters] = useState<{
    fromDate: string;
    toDate: string;
    selectedUniverses: string[];
    selectedCustomUniverses: string[];
    appliedFilters: FiltersFieldProps;
    searchSymbol: string;
  } | null>(null);
  const [viewMode, setViewMode] = useState<"peak" | "current">("peak");
  const [isHeatmapLoading, setIsHeatmapLoading] = useState(true);
  const [isTopPerformersLoading, setIsTopPerformersLoading] = useState(true);
  const [isRecordsLoading, setIsRecordsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [indexPerformance, setIndexPerformance] = useState<
    IndexPerformanceItem[]
  >([]);
  const [isIndexLoading, setIsIndexLoading] = useState(true);
  const [indexChart, setIndexChart] = useState<IndexChartData>({
    dates: [],
    series: [],
  });
  const [isIndexChartLoading, setIsIndexChartLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<PerformanceTab>("all");
  const { isDark } = useTheme();

  const visibleRecords = useMemo(
    () => (activeTab === "top" ? topPerformers : records),
    [activeTab, topPerformers, records],
  );

  /**
   * The page fills the viewport exactly so the app shell never scrolls; the
   * tables absorb the leftover height and scroll internally. Measured rather
   * than hard-coded because the chrome above differs per user — the market
   * ticker only renders for subscribed accounts.
   */
  const pageRef = useRef<HTMLDivElement>(null);
  const [pageHeight, setPageHeight] = useState<number | undefined>();

  useEffect(() => {
    const element = pageRef.current;
    if (!element) return;

    const measure = () => {
      const { top } = element.getBoundingClientRect();
      setPageHeight(Math.max(MIN_PAGE_HEIGHT, window.innerHeight - top - 12));
    };

    measure();
    window.addEventListener("resize", measure);
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(document.body);

    return () => {
      window.removeEventListener("resize", measure);
      resizeObserver.disconnect();
    };
  }, []);

  const [listSortConfig, setListSortConfig] = useState<SortConfig>({
    key: null,
    direction: null,
  });
  const [topSortConfig, setTopSortConfig] = useState<SortConfig>({
    key: null,
    direction: null,
  });

  const handleListSort = (columnKey: string) => {
    setListSortConfig((prev) => {
      if (prev.key !== columnKey) return { key: columnKey, direction: "desc" };
      if (prev.direction === "desc")
        return { key: columnKey, direction: "asc" };
      if (prev.direction === "asc") return { key: null, direction: null };
      return { key: columnKey, direction: "desc" };
    });
  };

  const handleTopSort = (columnKey: string) => {
    setTopSortConfig((prev) => {
      if (prev.key !== columnKey) return { key: columnKey, direction: "desc" };
      if (prev.direction === "desc")
        return { key: columnKey, direction: "asc" };
      if (prev.direction === "asc") return { key: null, direction: null };
      return { key: columnKey, direction: "desc" };
    });
  };

  const initialFromDate = useRef(
    moment().subtract(1, "days").subtract(1, "year").format("YYYY-MM-DD"),
  ).current;
  const initialToDate = useRef(
    moment().subtract(1, "days").format("YYYY-MM-DD"),
  ).current;

  const user = getLocalStorageUser();
  const token = user?.token;
  const hasCustomUniverseAccess =
    user?.plan === process.env.NEXT_PUBLIC_REQUIRED_PLAN;

  const { universes } = useCustomUniverses(token);

  const getAppliedFiltersParams = useCallback(
    (filtersProps: FiltersFieldProps) => {
      const params: FetchParams = {};
      Object.entries(filtersProps).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) params[key] = value.join(",");
        } else if (value !== "" && value !== null && value !== undefined) {
          params[key] = value;
        }
      });
      return params;
    },
    [],
  );

  /**
   * The date window actually sent to the data endpoints. A whole-month drill
   * narrows it to that month, clamped to the picked range so it can never
   * reach outside what the date-range picker allows. `heatmapDate` (a single
   * day) stays a separate query param layered on top of the full range
   * elsewhere, so this only reacts to `selectedMonth`.
   */
  const effectiveDateRange = useMemo(() => {
    if (!filters) return null;
    const monthRange = selectedMonth
      ? clampMonthToRange(selectedMonth, filters.fromDate, filters.toDate)
      : null;
    return monthRange ?? { fromDate: filters.fromDate, toDate: filters.toDate };
  }, [filters, selectedMonth]);

  /**
   * Universe selection is multi-select on both sides: index universes go out as
   * a comma-separated `indicesname`, the user's own universes as
   * `universe_name`. Selecting none means "no universe filter" rather than
   * "nothing", so the page shows everything instead of going blank.
   */
  const applyUniverseParams = useCallback(
    (params: FetchParams, current: NonNullable<typeof filters>) => {
      if (current.selectedUniverses.length > 0) {
        params.indicesname = current.selectedUniverses.join(",");
      }
      if (current.selectedCustomUniverses.length > 0) {
        params.universe_name = current.selectedCustomUniverses.join(",");
      }
      return params;
    },
    [],
  );

  const fetchHeatmap = useCallback(async () => {
    if (!filters) return;
    setIsHeatmapLoading(true);
    try {
      const filterParams = getAppliedFiltersParams(filters.appliedFilters);
      const params: FetchParams = {
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        ...filterParams,
      };
      if (filters.searchSymbol) params.symbol = filters.searchSymbol;
      applyUniverseParams(params, filters);
      const res = await performanceService.getHeatmap(params);
      setHeatmapData(res.data || []);
    } catch (err) {
      console.error("Failed to fetch heatmap:", err);
    } finally {
      setIsHeatmapLoading(false);
    }
  }, [filters, getAppliedFiltersParams, applyUniverseParams]);

  /**
   * Benchmark returns depend only on the date window — index levels are not
   * affected by the stock filters — so this deliberately ignores them. A
   * month drill still narrows the window; a single-day drill layers on top
   * via `heatmapDate` instead, since a day has no distinct first/last close.
   */
  const fetchIndexPerformance = useCallback(async () => {
    if (!filters || !effectiveDateRange) return;
    setIsIndexLoading(true);
    try {
      const params: FetchParams = {
        fromDate: effectiveDateRange.fromDate,
        toDate: effectiveDateRange.toDate,
      };
      if (heatmapDate) params.heatmapDate = heatmapDate;
      const res = await performanceService.getIndexPerformance(params);
      setIndexPerformance(res.data || []);
    } catch (err) {
      console.error("Failed to fetch index performance:", err);
      setIndexPerformance([]);
    } finally {
      setIsIndexLoading(false);
    }
  }, [filters, effectiveDateRange, heatmapDate]);

  /**
   * Daily benchmark series for the comparison chart. Driven by the date
   * window, so a month drill narrows it same as everywhere else; a
   * single-day drill is skipped since a one-day chart has nothing to plot.
   */
  const fetchIndexChart = useCallback(async () => {
    if (!filters || !effectiveDateRange) return;
    setIsIndexChartLoading(true);
    try {
      const res = await performanceService.getIndexChart({
        fromDate: effectiveDateRange.fromDate,
        toDate: effectiveDateRange.toDate,
      });
      setIndexChart({
        dates: res.data?.dates || [],
        series: res.data?.series || [],
      });
    } catch (err) {
      console.error("Failed to fetch index chart:", err);
      setIndexChart({ dates: [], series: [] });
    } finally {
      setIsIndexChartLoading(false);
    }
  }, [filters, effectiveDateRange]);

  /**
   * Responses can land out of order when the sort or a filter changes quickly;
   * only the latest request for each table is allowed to write its result.
   */
  const topRequestRef = useRef(0);
  const listRequestRef = useRef(0);

  const fetchTopPerformers = useCallback(async () => {
    if (!filters || !effectiveDateRange) return;
    const requestId = ++topRequestRef.current;
    setIsTopPerformersLoading(true);
    try {
      const filterParams = getAppliedFiltersParams(filters.appliedFilters);
      const params: FetchParams = {
        fromDate: effectiveDateRange.fromDate,
        toDate: effectiveDateRange.toDate,
        limit: TOP_PERFORMERS_LIMIT,
        ...filterParams,
      };
      if (heatmapDate) params.heatmapDate = heatmapDate;
      if (topSortConfig.key && topSortConfig.direction) {
        params.sortBy = topSortConfig.key;
        params.sortOrder = topSortConfig.direction;
      }
      if (filters.searchSymbol) params.symbol = filters.searchSymbol;
      applyUniverseParams(params, filters);
      const res = await performanceService.getTopPerformers(params);
      if (requestId !== topRequestRef.current) return;
      setTopPerformers(res.data || []);
    } catch (err) {
      if (requestId !== topRequestRef.current) return;
      console.error("Failed to fetch top performers:", err);
    } finally {
      if (requestId === topRequestRef.current) {
        setIsTopPerformersLoading(false);
      }
    }
  }, [
    filters,
    effectiveDateRange,
    heatmapDate,
    topSortConfig,
    getAppliedFiltersParams,
    applyUniverseParams,
  ]);

  const isLoadingMoreRef = useRef(isLoadingMore);
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  const fetchList = useCallback(
    async (pageNumber: number, reset: boolean = false) => {
      if (!filters || !effectiveDateRange) return;
      if (!reset && isLoadingMoreRef.current) return;
      // A reset (new sort/filter) supersedes anything in flight, including a
      // "load more" for the old ordering that would otherwise append stale rows.
      if (reset) listRequestRef.current += 1;
      const requestId = listRequestRef.current;
      if (reset) {
        setIsRecordsLoading(true);
        setIsLoadingMore(false);
      }
      if (!reset) setIsLoadingMore(true);

      try {
        const filterParams = getAppliedFiltersParams(filters.appliedFilters);
        const params: FetchParams = {
          page: pageNumber,
          limit: 20,
          fromDate: effectiveDateRange.fromDate,
          toDate: effectiveDateRange.toDate,
          ...filterParams,
        };
        if (heatmapDate) params.heatmapDate = heatmapDate;
        if (listSortConfig.key && listSortConfig.direction) {
          params.sortBy = listSortConfig.key;
          params.sortOrder = listSortConfig.direction;
        }
        if (filters.searchSymbol) params.symbol = filters.searchSymbol;
        applyUniverseParams(params, filters);

        const res = await performanceService.getList(params);
        if (requestId !== listRequestRef.current) return;
        if (reset) {
          setRecords(res.data || []);
          setHeader(res.header);
        } else {
          setRecords((prev) => [...prev, ...(res.data || [])]);
        }

        const currentPage = res.pagination?.page || pageNumber;
        if (res.pagination && res.pagination.totalPages > 0) {
          setHasMore(currentPage < res.pagination.totalPages);
        } else {
          setHasMore((res.data || []).length === params.limit);
        }

        setPage(currentPage);
      } catch (err) {
        if (requestId !== listRequestRef.current) return;
        console.error("Failed to fetch performance list:", err);
      } finally {
        if (requestId === listRequestRef.current) {
          setIsLoadingMore(false);
          setIsRecordsLoading(false);
        }
      }
    },
    [
      filters,
      effectiveDateRange,
      heatmapDate,
      listSortConfig,
      getAppliedFiltersParams,
      applyUniverseParams,
    ],
  );

  useEffect(() => {
    if (filters) {
      fetchHeatmap();
    }
  }, [filters, fetchHeatmap]);

  useEffect(() => {
    if (filters) {
      fetchIndexPerformance();
    }
  }, [filters, heatmapDate, fetchIndexPerformance]);

  useEffect(() => {
    if (filters) {
      fetchIndexChart();
    }
  }, [filters, fetchIndexChart]);

  useEffect(() => {
    if (filters) {
      fetchTopPerformers();
    }
  }, [filters, heatmapDate, topSortConfig, fetchTopPerformers]);

  useEffect(() => {
    if (filters) {
      fetchList(1, true);
    }
  }, [filters, heatmapDate, listSortConfig, fetchList]);

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      fetchList(page + 1);
    }
  };

  /** Range the fetches last ran against, to spot a real date-window change. */
  const appliedRangeRef = useRef<{ fromDate: string; toDate: string } | null>(
    null,
  );

  /**
   * Applying a filter (universe, sector, etc.) must not disturb a heatmap
   * drill-down — only an actual date-range change can invalidate it, by
   * moving the picked window out from under the selected day/month. The
   * comparison runs here rather than inside the `setFilters` updater, which
   * React may call more than once and so must stay free of side effects.
   */
  const handleFiltersChange = useCallback(
    (newFilters: NonNullable<typeof filters>) => {
      const { fromDate, toDate } = newFilters;
      const previousRange = appliedRangeRef.current;
      const rangeChanged =
        previousRange?.fromDate !== fromDate ||
        previousRange?.toDate !== toDate;
      appliedRangeRef.current = { fromDate, toDate };

      if (rangeChanged) {
        setHeatmapDate((date) =>
          date && isDateInRange(date, fromDate, toDate) ? date : null,
        );
        setSelectedMonth((month) =>
          month && monthIntersectsRange(month, fromDate, toDate) ? month : null,
        );
      }

      setFilters(newFilters);
    },
    [],
  );

  /**
   * The workbook mirrors the table on screen: the active tab, its sort order
   * and the current filters, with only the columns the table renders. Every
   * matching record is included rather than just the pages already scrolled
   * into view, so the file matches what the filters describe.
   */
  const handleExport = async () => {
    if (!filters || !effectiveDateRange || isExporting) return;
    setIsExporting(true);
    try {
      const filterParams = getAppliedFiltersParams(filters.appliedFilters);
      const params: FetchParams = {
        fromDate: effectiveDateRange.fromDate,
        toDate: effectiveDateRange.toDate,
        tab: activeTab,
        ...filterParams,
      };
      if (heatmapDate) params.heatmapDate = heatmapDate;

      const sortConfig = activeTab === "top" ? topSortConfig : listSortConfig;
      if (sortConfig.key && sortConfig.direction) {
        params.sortBy = sortConfig.key;
        params.sortOrder = sortConfig.direction;
      }
      // The Top Performers tab is fetched capped; the export matches that cap.
      if (activeTab === "top") params.limit = TOP_PERFORMERS_LIMIT;
      if (filters.searchSymbol) params.symbol = filters.searchSymbol;
      applyUniverseParams(params, filters);

      const response = await axios.get(
        performanceService.getExportUrl(params),
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          responseType: "blob",
        },
      );

      // An empty result set comes back as JSON rather than a workbook.
      const contentType = response.headers["content-type"] || "";
      if (
        contentType.includes("application/json") ||
        response.data.type === "application/json"
      ) {
        const json = JSON.parse(await response.data.text());
        alertService.error(
          json.message || json.error?.message || "Failed to export data",
        );
        return;
      }

      const contentDisposition = response.headers["content-disposition"];
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || "past-performance-export.xlsx";

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      // remove() is a no-op if the node is already detached, unlike
      // removeChild which throws.
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export performance data:", err);
      alertService.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  // The h-[calc(100dvh-9rem)] class below is only a first-paint approximation;
  // the measured pageHeight replaces it as soon as the effect runs. The pt-2
  // is headroom for the filter button's count badge, which sits above the
  // button and would otherwise be clipped by the overflow-hidden.
  return (
    <div
      ref={pageRef}
      style={pageHeight ? { height: pageHeight } : undefined}
      className="flex h-[calc(100dvh-9rem)] flex-col overflow-hidden bg-white px-2 pt-2 transition-colors duration-300 dark:bg-[#121419]"
    >
      <div className="flex min-h-0 flex-1 flex-col pb-1">
        {/* Header with filters aligned to the right side */}
        <div className="flex shrink-0 flex-col gap-1.5 pb-1 border-b border-[#d0d4e4] dark:border-[#394564] lg:flex-row lg:items-center lg:justify-between">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white whitespace-nowrap">
            Past Performance & Analysis
          </h2>
          <div className="flex min-w-0 flex-1 items-center justify-end">
            <PerformanceControls
              token={token}
              universes={universes}
              hasCustomUniverseAccess={hasCustomUniverseAccess}
              onFiltersChange={handleFiltersChange}
              initialFromDate={initialFromDate}
              initialToDate={initialToDate}
            />
          </div>
        </div>

        {/* gap keeps a hair of space on both sides of each section rule */}
        <div className="flex min-h-0 flex-1 flex-col gap-1.5">
          {/* 1. Daily Returns Heatmap (100% Full Width) */}
          <div className="flex-shrink-0 min-h-[12rem] h-[12rem] w-full min-w-0">
            <PerformanceHeatmap
              heatmapData={heatmapData}
              heatmapDate={heatmapDate}
              onDateSelect={(date) => {
                setHeatmapDate((prev) => (prev === date ? null : date));
                setSelectedMonth(null);
              }}
              selectedMonth={selectedMonth}
              onMonthSelect={(monthId) => {
                setSelectedMonth((prev) => (prev === monthId ? null : monthId));
                setHeatmapDate(null);
              }}
              dateRangeKey={
                filters ? `${filters.fromDate}_${filters.toDate}` : undefined
              }
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLoading={isHeatmapLoading}
            />
          </div>

          {/* 2. PerformanceStats Metric Cards Strip (Full Width Block directly after Heatmap) */}
          {/* py-1 keeps the strip off both rules, so each line breathes on both sides */}
          <div className="flex-shrink-0 border-y border-[#d0d4e4] py-1 dark:border-[#394564]">
            <PerformanceStats
              header={header ?? undefined}
              indexPerformance={indexPerformance}
              isIndexLoading={isIndexLoading}
              isLoading={isRecordsLoading}
              layout="row"
            />
          </div>

          {/* Bottom Row: Past Performance Table (50% Left) + Index Comparison Chart (50% Right) */}
          <div className="flex min-h-0 flex-1 flex-col border-t border-[#d0d4e4] pt-1 dark:border-[#394564] xl:flex-row xl:items-stretch">
            {/* Left 50%: Past Performance Table */}
            <div className="flex min-h-0 flex-1 min-w-0 flex-col pb-1 xl:w-1/2 xl:pb-0 xl:pr-2">
              <PastPerformanceTable
                records={visibleRecords}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                isLoading={
                  activeTab === "top"
                    ? isTopPerformersLoading
                    : isRecordsLoading
                }
                sortConfig={
                  activeTab === "top" ? topSortConfig : listSortConfig
                }
                onSort={activeTab === "top" ? handleTopSort : handleListSort}
                onExport={handleExport}
                isExporting={isExporting}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                topPerformerCount={topPerformers.length}
              />
            </div>

            {/* Right 50%: Index Comparison Chart — split from the table by a single rule */}
            <div className="flex min-h-[16rem] flex-1 min-w-0 flex-col border-t border-[#d0d4e4] pt-1 dark:border-[#394564] xl:h-full xl:w-1/2 xl:border-l xl:border-t-0 xl:pl-2 xl:pt-0">
              <IndexComparisonChart
                dates={indexChart.dates}
                series={indexChart.series}
                isLoading={isIndexChartLoading}
                isDark={isDark}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PastPerformancePage);
